/* ============================================================
   PARCU — the measurement diagram on the size charts

   Puts the right measuring diagram beside the size chart on the
   sizing page, and under the chart in the Sizing tab of the
   product page. parcu-sizeimg.css does the placing; this only
   decides which diagram and where to insert it.

   Three diagrams, because a classic tee and a boxy tee are
   measured differently and a hoodie differently again. Which one
   shows follows the same toggles the chart follows, and it is read
   off the chart that is showing rather than off the button that
   was pressed, so it is right however the state was set.

   On a product page a tee now shows both tee diagrams at once,
   side by side, because the difference between the cuts is body
   length against chest width and that only reads as a comparison.
   The selected fit is the one whose caption is lit; the other
   stays as a quiet grey note. The sizing page still swaps a single
   larger diagram, where there is room for one drawing and not for
   two. The hoodie has one cut, so it gets one diagram and no
   caption.

   This file also carries the two things about that tab that cannot
   be done from inside the snippet: the fit line repeated under the
   Fit pills in the buy box, and the bolding of the figures in the
   Shipping and Materials tabs, which has to find them by shape
   because the sentences around them are translated per country.

   Both charts live in snippets too large to write through the
   theme API, so they are adapted from the outside. See the header
   of the stylesheet.

   The file urls and the product type are printed server side into
   PARCU_SIZEIMG, so swapping a diagram is a matter of replacing
   the file in Settings > Files under the same name.
   ============================================================ */

(function () {
    'use strict';

    var CFG = window.PARCU_SIZEIMG;
    if (!CFG || !CFG.classic) return;

    var TAG = { classic: 'Classic', boxy: 'Boxy', hoodie: '' };

    function src(kind) {
        return CFG[kind] || CFG.classic;
    }

    /* The caption is read off the fit toggle rather than written in
       English here, because the toggle is printed server side in the
       country's language. The constants above are only a fallback
       for a page that has no toggle. */
    function label(kind) {
        var btn = document.querySelector('.parcu-fit-btn[data-fit="' + kind + '"]');
        var t = btn && btn.textContent ? btn.textContent.trim() : '';
        return t || TAG[kind] || '';
    }

    function figure(kind, extra) {
        var f = document.createElement('figure');
        f.className = 'parcu-sizeimg' + (extra ? ' ' + extra : '');

        var box = document.createElement('span');
        box.className = 'parcu-sizeimg__box';

        var i = document.createElement('img');
        i.setAttribute('loading', 'lazy');
        i.setAttribute('decoding', 'async');
        i.setAttribute('alt', '');
        i.src = src(kind);

        var tag = document.createElement('span');
        tag.className = 'parcu-sizeimg__tag';
        tag.textContent = label(kind);

        box.appendChild(i);
        box.appendChild(tag);
        f.appendChild(box);
        f.dataset.kind = kind;
        return f;
    }

    /* Guarded on the kind rather than on the current src, so a
       caption the translation layer has already rewritten is not
       reset to English every time a class changes somewhere. */
    function show(fig, kind) {
        if (fig.dataset.kind === kind) return;
        fig.dataset.kind = kind;
        var im = fig.querySelector('img');
        if (im) im.setAttribute('src', src(kind));
        var tag = fig.querySelector('.parcu-sizeimg__tag');
        if (tag) tag.textContent = TAG[kind] || '';
    }

    function on(el) {
        return !!(el && el.classList.contains('active'));
    }

    /* ------------------------------------------------------------
       THE SIZING PAGE
       One chart block is active at a time and each has a fixed id,
       so the pair of toggles above them does not have to be read.
       ------------------------------------------------------------ */

    function pageKind() {
        if (on(document.getElementById('chart-hoodie'))) return 'hoodie';
        if (on(document.getElementById('chart-tshirt-boxy'))) return 'boxy';
        return 'classic';
    }

    function sizingPage() {
        var row = document.querySelector('.parcu-size-wrap .toggle-row');
        if (!row) return;
        var wrap = row.parentNode;
        if (!wrap || wrap.parcuDone) return;
        wrap.parcuDone = true;
        wrap.classList.add('parcu-sizepage');

        var fig = figure(pageKind());
        var foot = wrap.querySelector('.sc-footnote');
        if (foot) { wrap.insertBefore(fig, foot); } else { wrap.appendChild(fig); }

        if (!window.MutationObserver) return;
        new MutationObserver(function () { show(fig, pageKind()); })
            .observe(wrap, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }

    /* ------------------------------------------------------------
       THE PRODUCT PAGE

       The tab is found by the chart inside it, not by its heading,
       because the heading is translated per country and matching on
       the word Sizing would only work in English.

       A tee tab holds two charts in fit order, classic then boxy,
       and hides the one that is not showing. A hoodie has one cut
       and one chart, so the product type settles it.
       ------------------------------------------------------------ */

    function tabKind(body) {
        if (/hood/i.test(CFG.type || '')) return 'hoodie';
        var wraps = body.querySelectorAll('.parcu-size-wrap');
        for (var i = 0; i < wraps.length; i++) {
            if (!wraps[i].classList.contains('parcu-hidden')) {
                return i === 1 ? 'boxy' : 'classic';
            }
        }
        return 'classic';
    }

    /* Above the measurement disclaimer, not after it. The
       disclaimer is a footnote about the numbers in the chart, so
       the drawing that explains those numbers belongs between the
       two. A footnote that is not last is not a footnote. */
    function place(body, el) {
        var foot = body.querySelector('.parcu-footnote');
        if (foot) { body.insertBefore(el, foot); } else { body.appendChild(el); }
    }

    function productTab() {
        var chart = document.querySelector('.parcu-tab__body .parcu-size-wrap');
        if (!chart) return;
        var body = chart.closest('.parcu-tab__body');
        if (!body || body.parcuDone) return;
        body.parcuDone = true;

        /* A hoodie has one cut, so there is nothing to compare it
           against and it keeps the single diagram. */
        if (/hood/i.test(CFG.type || '')) {
            place(body, figure('hoodie', 'parcu-sizeimg--tab'));
            return;
        }

        /* A tee shows both drawings at once. The cuts differ in body
           length against chest width, and that is a difference you
           can only read by seeing them next to each other, so the
           pair is worth more than one larger drawing that swaps. */
        var pair = document.createElement('div');
        pair.className = 'parcu-sizeimg-pair parcu-sizeimg--tab';

        var figs = { classic: figure('classic'), boxy: figure('boxy') };
        pair.appendChild(figs.classic);
        pair.appendChild(figs.boxy);
        place(body, pair);

        /* Captions are re-read on every pass because the translation
           layer rewrites the toggle after this has already run. */
        function captions() {
            var kinds = ['classic', 'boxy'];
            for (var i = 0; i < kinds.length; i++) {
                var t = figs[kinds[i]].querySelector('.parcu-sizeimg__tag');
                var l = label(kinds[i]);
                if (t && l && t.textContent !== l) t.textContent = l;
            }
        }

        /* Which caption is lit follows the chart that is showing, not
           the button that was pressed, so it is right however the
           state was set. Guarded on the last value so toggling a
           class here cannot feed the observer below back into itself. */
        var last = null;
        function mark() {
            captions();
            var k = tabKind(body);
            if (k === last) return;
            last = k;
            figs.classic.classList.toggle('is-active', k === 'classic');
            figs.boxy.classList.toggle('is-active', k === 'boxy');
        }
        mark();

        if (!window.MutationObserver) return;
        new MutationObserver(mark)
            .observe(body, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }

    /* ------------------------------------------------------------
       THE FIT LINE IN THE BUY BOX

       The same sentence the Sizing tab carries, under the Fit pills
       where the choice is actually made. Most shoppers never open
       the tab.

       The fieldset is found by its option values being exactly
       Classic and Boxy, not by its legend, because the legend is
       translated per country while the option values are product
       data and are the same everywhere. The wording comes from
       PARCU_FITLINE, printed by the collapsible, so there is one
       source for it and nothing to keep in sync.
       ------------------------------------------------------------ */

    function fitFieldset() {
        var sets = document.querySelectorAll('fieldset');
        for (var i = 0; i < sets.length; i++) {
            var ins = sets[i].querySelectorAll('input[type="radio"]');
            if (ins.length !== 2) continue;
            var seen = {};
            for (var j = 0; j < ins.length; j++) seen[String(ins[j].value).toLowerCase()] = 1;
            if (seen.classic && seen.boxy) return sets[i];
        }
        return null;
    }

    function buyBoxLine() {
        var L = window.PARCU_FITLINE;
        if (!L || !L.classic) return;
        var set = fitFieldset();
        /* The theme re-renders the picker on a variant change, so the
           flag is kept on the fieldset rather than in a variable: a
           replaced fieldset arrives without it and gets a new line. */
        if (!set || set.parcuLine) return;
        set.parcuLine = true;

        var p = document.createElement('p');
        p.className = 'parcu-fitnote';
        set.parentNode.insertBefore(p, set.nextSibling);

        function write() {
            var on = set.querySelector('input[type="radio"]:checked');
            var html = (on && /boxy/i.test(on.value)) ? L.boxy : L.classic;
            if (p.innerHTML !== html) p.innerHTML = html;
        }
        write();
        set.addEventListener('change', write);
    }

    /* ------------------------------------------------------------
       THE FIGURES IN THE TABS

       Shipping cost, delivery window, weight and fibre, picked out
       of their sentences. By shape rather than by word, because the
       sentence is different in every market and the figure is not.
       The footnote is left alone: it is a caveat about the numbers,
       not a number worth finding.
       ------------------------------------------------------------ */

    var FIGURE = '\\d+(?:[.,]\\d+)?\\s?(?:gsm|GSM)'
        + '|\\d+\\s?%'
        + '|[$\u20ac\u00a3\u00a5\u20a9]\\s?\\d[\\d.,]*'
        + '|\\d[\\d.,]*\\s?(?:AUD|USD|GBP|EUR|NZD|CAD|SGD|HKD|TWD|JPY|KRW|MYR|AED|SAR|QAR|CHF|SEK|NOK|DKK|ISK|PLN|CZK|HUF|RON|MXN|CLP)'
        + '|\\d+\\s?[\u2013-]\\s?\\d+';

    function boldFigures() {
        var ps = document.querySelectorAll('.parcu-tab__body p');
        for (var i = 0; i < ps.length; i++) {
            var p = ps[i];
            if (p.parcuBold || p.classList.contains('parcu-footnote')) continue;
            p.parcuBold = true;

            var walk = document.createTreeWalker(p, NodeFilter.SHOW_TEXT, null);
            var hits = [], n;
            while ((n = walk.nextNode())) {
                if (n.parentNode && n.parentNode.closest('strong, a')) continue;
                if (new RegExp(FIGURE).test(n.nodeValue)) hits.push(n);
            }

            for (var k = 0; k < hits.length; k++) {
                var t = hits[k], re = new RegExp(FIGURE, 'g');
                var frag = document.createDocumentFragment(), last = 0, m;
                while ((m = re.exec(t.nodeValue))) {
                    if (m.index > last) frag.appendChild(document.createTextNode(t.nodeValue.slice(last, m.index)));
                    var b = document.createElement('strong');
                    b.textContent = m[0];
                    frag.appendChild(b);
                    last = m.index + m[0].length;
                }
                if (last < t.nodeValue.length) frag.appendChild(document.createTextNode(t.nodeValue.slice(last)));
                if (t.parentNode) t.parentNode.replaceChild(frag, t);
            }
        }
    }

    /* The fieldset holding the sizes. Found by its values rather than
       by the word above them, because that word is translated and the
       sizes are not. */
    function sizeFieldset() {
        var SIZES = { xxs: 1, xs: 1, s: 1, m: 1, l: 1, xl: 1, xxl: 1, '2xl': 1, '3xl': 1 };
        var sets = document.querySelectorAll('fieldset');
        for (var i = 0; i < sets.length; i++) {
            var ins = sets[i].querySelectorAll('input[type="radio"]');
            if (ins.length < 2) continue;
            var hit = 0;
            for (var j = 0; j < ins.length; j++) {
                if (SIZES[String(ins[j].value).trim().toLowerCase()]) hit++;
            }
            if (hit === ins.length) return sets[i];
        }
        return null;
    }

    /* The advice belongs where the decision is made. It is the same
       sentence the collapsible opens with, published from there so
       there is one wording and one set of translations. */
    function sizeNote() {
        var L = window.PARCU_FITLINE;
        if (!L || !L.tts) return;
        var set = sizeFieldset();
        if (!set || set.parcuNote) return;
        var legend = set.querySelector('legend');
        if (!legend) return;
        set.parcuNote = true;
        var note = document.createElement('span');
        note.className = 'parcu-sizenote';
        note.textContent = L.tts;
        legend.appendChild(note);
    }

    /* ------------------------------------------------------------
       FIND YOUR FIT

       A line under the size pills that opens the model gallery and
       the calculator over the product, so the question "which size
       am I" can be answered without leaving the page and losing the
       colour and fit already chosen.

       The contents are fetched the first time it is opened, from a
       section that renders the same two snippets the sizing page
       does. The calculator is 177KB of Liquid; printing it into
       every product page to serve the few who open it would be a
       poor trade. The anchor still points at the sizing page, so it
       works with no script and reads as a real link to a crawler.
       ------------------------------------------------------------ */

    var popUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (popUrl.charAt(popUrl.length - 1) !== '/') popUrl += '/';
    popUrl += '?section_id=parcu-sizepop';

    var pop, popBody, popJob, fillJob;

    /* Six to a screen rather than twelve while the panel is the host:
       it is a drawer over a product, not the sizing page, and half the
       pictures means half the wait. The gallery reads this before it
       draws; the sizing page sets nothing and keeps its twelve. */
    window.PARCU_PSG_PAGE = 6;

    function load() {
        if (popJob) return popJob;
        popJob = fetch(popUrl, { headers: { Accept: 'text/html' } })
            .then(function (res) { return res.ok ? res.text() : ''; })
            .catch(function () { return ''; });
        return popJob;
    }

    /* A script inserted as markup is inert. Each one is replaced with
       a fresh element so the browser runs it, in the order it was
       written, and the data block is left alone because it is data. */
    function wake(root) {
        var tags = root.querySelectorAll('script');
        for (var i = 0; i < tags.length; i++) {
            var old = tags[i];
            var type = (old.getAttribute('type') || '').toLowerCase();
            if (type && type.indexOf('javascript') === -1 && type !== 'module') continue;
            var fresh = document.createElement('script');
            for (var a = 0; a < old.attributes.length; a++) {
                fresh.setAttribute(old.attributes[a].name, old.attributes[a].value);
            }
            fresh.text = old.textContent;
            old.parentNode.replaceChild(fresh, old);
        }
    }

    function popup() {
        if (pop) return pop;
        pop = document.createElement('dialog');
        pop.className = 'parcu-sizepop';

        var x = document.createElement('button');
        x.type = 'button';
        x.className = 'parcu-sizepop__x';
        x.setAttribute('aria-label', (window.PARCU_FITLINE && window.PARCU_FITLINE.close) || 'Close');
        pop.appendChild(x);

        popBody = document.createElement('div');
        popBody.className = 'parcu-sizepop__scroll';
        pop.appendChild(popBody);

        document.body.appendChild(pop);

        x.addEventListener('click', function () { pop.close(); });
        /* The panel fills the dialog, so a click on the dialog itself
           is a click on the margin around it. */
        pop.addEventListener('click', function (e) { if (e.target === pop) pop.close(); });
        pop.addEventListener('close', function () {
            document.documentElement.classList.remove('parcu-sizepop-on');
        });
        return pop;
    }

    /* Filling the panel while it is still shut is what makes it open
       at once rather than after a fetch, a parse and two scripts. */
    function fill() {
        if (fillJob) return fillJob;
        var d = popup();
        fillJob = load().then(function (html) {
            if (!html || d.parcuFilled) return d.parcuFilled;
            d.parcuFilled = true;
            d.classList.remove('is-loading');
            popBody.innerHTML = html;
            wake(popBody);
            return true;
        });
        return fillJob;
    }

    /* A lazy picture inside a shut dialog is never fetched, which is
       why the gallery used to arrive blank and fill in. The first
       screenful is switched to eager at low priority, so it comes
       down behind the page's own work and is decoded and waiting. */
    function warmShots() {
        if (!popBody || popBody.parcuWarm) return;
        var imgs = popBody.querySelectorAll('.psg__card img');
        if (!imgs.length) return;
        popBody.parcuWarm = true;
        for (var i = 0; i < Math.min(8, imgs.length); i++) {
            imgs[i].setAttribute('fetchpriority', 'low');
            imgs[i].setAttribute('loading', 'eager');
        }
    }

    function warmPop() {
        fill().then(warmShots);
    }

    /* What the shopper is looking at, handed to the panel so it opens
       on people wearing the same thing in the same size rather than on
       the top of the list. The fit comes off the Fit pills when the
       product has them and is a hoodie when it does not, because only
       the tees are cut two ways. */
    function picked() {
        var out = { fit: '', size: '' };

        var fits = fitFieldset();
        if (fits) {
            var on = fits.querySelector('input[type="radio"]:checked');
            if (on) out.fit = /boxy/i.test(on.value) ? 'boxy' : 'classic';
        } else if (window.PARCU_FITLINE && !window.PARCU_FITLINE.classic) {
            out.fit = 'hoodie';
        }

        var sizes = sizeFieldset();
        if (sizes) {
            var s = sizes.querySelector('input[type="radio"]:checked');
            if (s) out.size = String(s.value).trim().toUpperCase();
        }
        return out;
    }

    function preselect() {
        if (!popBody) return;
        var want = picked();
        if (want.fit) {
            var fp = popBody.querySelector('.psg__pill[data-fit="' + want.fit + '"]');
            if (fp && !fp.classList.contains('is-on')) fp.click();
        }
        if (want.size) {
            var sp = popBody.querySelector('.psg__pill[data-size="' + want.size + '"]');
            /* A size nobody is wearing in this cut is left alone, so the
               panel opens on something rather than on nothing. */
            if (sp && !sp.disabled && !sp.classList.contains('is-on')) sp.click();
        }
    }

    function openPop() {
        var d = popup();
        document.documentElement.classList.add('parcu-sizepop-on');
        if (!d.parcuFilled) d.classList.add('is-loading');
        if (!d.open) d.showModal();
        fill().then(function (ok) {
            warmShots();
            preselect();
            if (ok || d.parcuFilled) return;
            /* Nothing came back. Rather than leave an empty box open,
               send them to the page that has the same thing. */
            window.location.href = '/pages/sizing';
        });
    }

    /* Only the name of the thing is the link. The words around it are
       a sentence, not a control, and underlining the lot makes the
       whole line look like one long button. */
    function sizeLink() {
        var L = window.PARCU_FITLINE;
        if (!L || !L.calc_link) return;
        var set = sizeFieldset();
        if (!set || set.parcuLink) return;
        set.parcuLink = true;

        var p = document.createElement('p');
        p.className = 'parcu-sizelink';

        if (L.calc) p.appendChild(document.createTextNode(L.calc + ' '));
        var a = document.createElement('a');
        a.href = '/pages/sizing';
        a.textContent = L.calc_link;
        p.appendChild(a);
        if (L.calc_end) p.appendChild(document.createTextNode(' ' + L.calc_end));

        set.parentNode.insertBefore(p, set.nextSibling);
    }

    /* Every route to the sizing page from inside a product page opens
       the panel instead: the line under the size pills, and the link
       in the sizing tab of the collapsible. Delegated, so it covers
       the collapsible without that snippet having to know about any
       of this, and it still falls through to the real page if the
       panel cannot be built. */
    document.addEventListener('click', function (e) {
        var a = e.target.closest ? e.target.closest('a[href*="/pages/sizing"]') : null;
        if (!a) return;
        if (!a.closest('.parcu-sizelink, .parcu-tab__body')) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
        e.preventDefault();
        openPop();
    });

    /* Warm it on the way in, both the markup and the first pictures. */
    document.addEventListener('pointerover', function (e) {
        var a = e.target.closest ? e.target.closest('a[href*="/pages/sizing"]') : null;
        if (a && a.closest('.parcu-sizelink, .parcu-tab__body')) warmPop();
    });
    document.addEventListener('pointerdown', function (e) {
        var a = e.target.closest ? e.target.closest('a[href*="/pages/sizing"]') : null;
        if (a && a.closest('.parcu-sizelink, .parcu-tab__body')) warmPop();
    }, true);

    /* Once the page has finished its own work, the panel quietly
       builds itself. By the time anybody taps the line it is already
       there, which on a phone is the whole difference: there is no
       hover to warm it, and a tap gives perhaps a tenth of a second
       of notice. Idle time only, so it never races the product. */
    function prebuild() {
        if (!sizeFieldset() && !document.querySelector('.parcu-tab__body')) return;
        if (window.requestIdleCallback) {
            window.requestIdleCallback(function () { fill(); }, { timeout: 6000 });
        } else {
            setTimeout(fill, 2500);
        }
    }

    if (document.readyState === 'complete') {
        prebuild();
    } else {
        window.addEventListener('load', prebuild);
    }

    function run() {
        sizingPage();
        productTab();
        buyBoxLine();
        sizeNote();
        sizeLink();
        boldFigures();
    }

    function start() {
        run();
        /* The collapsible builds its tabs after load, so the first
           pass can miss them. Debounced, and both halves no-op once
           they have run. */
        if (!window.MutationObserver) return;
        var timer;
        new MutationObserver(function () {
            clearTimeout(timer);
            timer = window.setTimeout(run, 120);
        }).observe(document.body, { childList: true, subtree: true });
        window.addEventListener('load', run);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
